import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

export type AppRole = "developer" | "master" | "super_admin" | "admin" | "editor" | "viewer";

const ROLE_HIERARCHY: Record<AppRole, number> = {
  developer: 5,
  master: 4,
  super_admin: 3,
  admin: 2,
  editor: 1,
  viewer: 0,
};

export const getRoleLevel = (role: AppRole) => ROLE_HIERARCHY[role] ?? -1;
export const canActOn = (actorRole: AppRole, targetRole: AppRole) => getRoleLevel(actorRole) > getRoleLevel(targetRole);

interface AdminSettings {
  global_read_only: boolean;
  access_blocked: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  loading: boolean;
  adminSettings: AdminSettings;
  refreshAdminSettings: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminSettings, setAdminSettings] = useState<AdminSettings>({ global_read_only: false, access_blocked: false });

  const fetchRole = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();
    setRole((data?.role as AppRole) ?? null);
  }, []);

  const refreshAdminSettings = useCallback(async () => {
    const { data } = await supabase
      .from("admin_settings")
      .select("global_read_only, access_blocked")
      .eq("id", "00000000-0000-0000-0000-000000000001")
      .maybeSingle();
    if (data) {
      setAdminSettings({ global_read_only: data.global_read_only, access_blocked: data.access_blocked });
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => fetchRole(session.user.id), 0);
        } else {
          setRole(null);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchRole(session.user.id);
      }
      setLoading(false);
    });

    refreshAdminSettings();

    return () => subscription.unsubscribe();
  }, [fetchRole, refreshAdminSettings]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/admin/reset-password`,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, role, loading, adminSettings, refreshAdminSettings, signIn, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
};
